<script lang="ts">
  import { getStravaActivityUploadFieldDefaults, getStravaActivityUploadStatus, sendActivityToStrava, uploadActivityStravaReport, type StravaActivityUploadFieldsResponse, type StravaActivityUploadStatusResponse } from '@/Util/api';
  import { Button } from '@/lib/components/ui/button';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import * as Dialog from '$lib/components/ui/dialog';
  import Input from '@/lib/components/ui/input/input.svelte';
  import { convertSeconds, kmToMile } from '@/Util/utils';
  import Textarea from '@/lib/components/ui/textarea/textarea.svelte';
  import * as Popover from "$lib/components/ui/popover";
  import type { TodaysActivities } from '@/Common/constants';
  import { Checkbox } from '@/lib/components/ui/checkbox';
  import { Label } from '@/lib/components/ui/label';
  import { cn } from '@/lib/utils';
  import type { CalendarLoader } from '../Calendar/CalendarLoaderNew';
  import { notifyApiError } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';
  import ReportDesigner from '@/SocialReports/ReportDesigner.svelte';
  import type { ReportConfig } from '@/SocialReports/types';

  export let activity: Exercise;
  export let disabled: boolean = false;
  export let className: string = '';
  export let todaysActivities: TodaysActivities | null = null;
  export let calendarLoader: CalendarLoader | null = null;

  let awaitingSendResponse = false;
  let awaitingStatusResponse = false;
  let awaitingDefaultsResponse = false;
  let status: StravaActivityUploadStatusResponse | null = null;
  let fieldDefaults: StravaActivityUploadFieldsResponse | null = null;
  let sendButtonIcon = 'fa-paper-plane';

  let openInStravaConfirmationPopoverOpen: boolean = false;

  let name: string = '';
  let description: string = '';
  let includeReport: boolean = true;
  let reportConfig: ReportConfig | null = null;

  let dialogOpen = false;
  let dialogMode: 'send' | 'update_report' = 'send';

  //@ts-ignore
  const Xert = window.Xert;
  let useFreedomUnits: boolean = Xert?._displayUnits === 'imperial';
  $: distanceVal = activity?.distance ? (useFreedomUnits ? kmToMile(activity.distance) : activity.distance) : 0;
  $: distanceUnit = useFreedomUnits ? 'mi' : 'km';
  $: durationStr = convertSeconds(activity?.duration ?? activity?.adu, false);
  $: avgPower = typeof activity?.avg_power === 'number' ? Math.round(activity.avg_power) : null;
  $: maxPower = typeof activity?.max_power === 'number' ? Math.round(activity.max_power) : null;
  $: speedUnit = useFreedomUnits ? 'mph' : 'km/h';
  $: avgSpeed = typeof activity?.avg_speed === 'number' ? (useFreedomUnits ? (activity.avg_speed * 0.621371) : activity.avg_speed) : null;
  $: maxSpeed = typeof activity?.max_speed === 'number' ? (useFreedomUnits ? (activity.max_speed * 0.621371) : activity.max_speed) : null;

  async function onSend() {
    sendButtonIcon = 'fa-spinner-third animate-spin';
    awaitingSendResponse = true;
    const report = reportConfig ?? undefined;
    const response = dialogMode == 'send' ? 
      await sendActivityToStrava(
        activity.path,
        { name: name || null, description: description || null, include_report: includeReport, report: includeReport ? report : undefined },
      )
    :
      await uploadActivityStravaReport(
        activity.path,
        report,
      );

    dialogOpen = false;
    awaitingSendResponse = false;
    sendButtonIcon = response.success ? 'fa-check' : 'fa-xmark';
    if (!response.success) {
      notifyApiError(response, dialogMode == 'send'
        ? { code: ErrorCode.STRAVA_SEND_FAILED, message: 'Could not send this activity to Strava. Please try again, or reconnect Strava in your Sync settings.' }
        : { code: ErrorCode.STRAVA_REPORT_FAILED, message: 'Could not update the Xert report on your Strava activity. Please try again in a moment.' });
    }
    if (response.success) {
      calendarLoader?.updateActivity(activity, (a) => (
        { ...a, strava_upload_id: response.id || a.strava_upload_id, name: name || fieldDefaults?.body_params?.name || a.name }
      ));
      todaysActivities?.update((prev) => {
        return prev.map((a) => {
          if (a.path === activity.path) {
            return { ...a, strava_upload_id: response.id || a.strava_upload_id, name: name || fieldDefaults?.body_params?.name || a.name };
          }
          return a;
        });
      });
    }
  }

  async function getStatus() {
    awaitingStatusResponse = true;
    status = await getStravaActivityUploadStatus(activity.path);
    activity.external_id = status.status_code == 'deleted' ? null : status?.activity_id || activity.external_id;
    awaitingStatusResponse = false;
  }

  async function getFieldDefaults() {
    awaitingDefaultsResponse = true;
    fieldDefaults = await getStravaActivityUploadFieldDefaults(activity.path);
    name = fieldDefaults?.body_params?.name || '';
    description = fieldDefaults?.body_params?.description || '';
    // Pre-ticked when this activity would get a report automatically; any activity can have one.
    includeReport = !!fieldDefaults?.body_params?.report_type;
    awaitingDefaultsResponse = false;
  }

  async function onClickStravaButton() {
    await Promise.all([getFieldDefaults(), getStatus()]);
    if (status?.status_code == 'not_sent' || status?.status_code == 'deleted') {
      // If status is not sent or deleted, open send dialog
      openInStravaConfirmationPopoverOpen = false;
      dialogOpen = true;
      dialogMode = 'send';
    }
  }

  async function onOpenInStrava() {
    if (status?.activity_url) {window.open(status.activity_url, '_blank');}
    else if (activity.external_id) {window.open(`https://www.strava.com/activities/${activity.external_id}`, '_blank');}
  }

  async function onOpenSendToStravaDialog() {
    dialogOpen = true;
    dialogMode = 'send';
    await Promise.all([getFieldDefaults(), getStatus().then(() => {
      if (status?.status_code == 'ready') {
        dialogMode = 'update_report';
      }
    })]);
    sendButtonIcon = 'fa-paper-plane';
  }

  async function onOpenUpdateStravaReportDialog() {
    dialogOpen = true;
    dialogMode = 'update_report';
    sendButtonIcon = 'fa-paper-plane';
  }

  $: showDesigner = dialogMode == 'update_report' || (!awaitingDefaultsResponse && !!fieldDefaults && includeReport);

</script>

{#if Activity.isRecorded({...activity, activity_mode: activity.activity_mode})}
  {#if activity.strava_upload_id}
    <Popover.Root bind:open={openInStravaConfirmationPopoverOpen} portal="#shadcn-ui-dialog-target">
      <Popover.Trigger asChild let:builder>
        <Button builders={[builder]} size='sm' variant='outline' disabled={disabled} class={className} on:click={(e) => { e.stopPropagation(); onClickStravaButton(); }}>
          <i class='fa-brands fa-strava text-xs mr-1' /> Strava
        </Button>
      </Popover.Trigger>
      <Popover.Content side='top' class="flex flex-col gap-2 max-h-[95dvh] max-w-[330px] w-[90vw] overflow-y-auto rounded-xl border-0 border-solid border-t-[5px] border-orange-500 p-4">
        <div class='flex flex-col w-full gap-2 items-center'>
          {#if awaitingStatusResponse}
            <div class='flex flex-row gap-2 text-sm items-center'>
              <i class="fa-solid fa-spinner fa-spin text-gray-400 h-max" />
              <div>Fetching Strava upload status...</div>
            </div>
          {:else}
            {#if status}
              <div class='flex flex-col gap-1'>
                <div class='flex flex-row gap-2 text-sm items-center'>
                  <i class={`fa-regular ${status.status_code == 'ready' ? 'fa-link' : 'fa-link-slash'} h-max`} />
                  <div>{status.status ?? status.message}</div>
                </div>
                <!-- {#if status.status}
                  <div class='text-sm'>Status: {status.status}</div>
                {/if} -->
                {#if status.error}
                  <div class='text-red-600 text-sm'>Error: {status.error}</div>
                {/if}
              </div>
            {/if}
          {/if}
          <div class='flex flex-row gap-2 w-full items-center'>
            {#if awaitingStatusResponse || awaitingDefaultsResponse}
              <i class="fa-solid fa-spinner fa-spin text-gray-400 h-max" />
            {:else}
              {#if status?.status_code == 'ready'}
                <Button variant='outline' size='sm' class='flex-1' on:click={() => {openInStravaConfirmationPopoverOpen = false; onOpenUpdateStravaReportDialog();}}>
                  <i class={`fa-solid fa-pencil text-xs mr-1`} /> Update Strava Report
                </Button>
              {:else if status?.status_code == 'not_sent' || status?.status_code == 'deleted' || status?.status_code == 'error'}
                <Button variant='outline' size='sm' class='flex-1' on:click={() => {openInStravaConfirmationPopoverOpen = false;  dialogOpen = true; dialogMode = 'send'; sendButtonIcon = 'fa-paper-plane';}}>
                  <i class={`fa-solid fa-paper-plane text-xs mr-1`} /> Send to Strava
                </Button>
              {/if}
            {/if}
            <Button size='sm' disabled={awaitingStatusResponse && !activity.external_id} class='flex-1 bg-orange-500 hover:bg-orange-400' on:click={() => {openInStravaConfirmationPopoverOpen = false; onOpenInStrava();}}>
              <i class={`fa-brands fa-strava text-xs mr-1`} /> Open in Strava
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  {:else if activity.external_id}
    <Button size='sm' variant='outline' disabled={disabled} class={className} on:click={(e) => { e.stopPropagation(); onOpenInStrava(); }}>
      <i class='fa-brands fa-strava text-xs mr-1' /> Open in Strava
    </Button>
  {:else}
    <Button size='sm' variant='outline' disabled={disabled || awaitingSendResponse} class={className} on:click={(e) => { e.stopPropagation(); onOpenSendToStravaDialog(); }}>
      <i class={`fa-solid ${sendButtonIcon} text-xs mr-1`} /> Send to Strava
    </Button>
  {/if}
  
  <Dialog.Root portal="#shadcn-ui-dialog-target" bind:open={dialogOpen}>
    <Dialog.Content class={cn("flex flex-col gap-4 max-h-[95dvh] w-[95vw] overflow-y-hidden rounded-xl border-0 border-solid border-t-[5px] border-orange-500", showDesigner ? 'max-w-[920px]' : 'max-w-[600px]')}>
      <div class='flex flex-col gap-4 overflow-y-auto'>
        {#if awaitingStatusResponse}
          <div class='flex flex-row gap-2 text-sm items-center'>
            <i class="fa-solid fa-spinner fa-spin text-gray-400 h-max" />
            <div>Fetching Strava upload status...</div>
          </div>
        {:else}
          {#if status}
            <div class='flex flex-col gap-1'>
              <div class='flex flex-row gap-2 text-sm items-center'>
                <i class={`fa-regular ${status.status_code == 'ready' ? 'fa-link' : 'fa-link-slash'} h-max`} />
                <div>{status.status ?? status.message}</div>
              </div>
              <!-- {#if status.status}
                <div class='text-sm'>Status: {status.status}</div>
              {/if} -->
              {#if status.error}
                <div class='text-red-600 text-sm'>Error: {status.error}</div>
              {/if}
            </div>
          {/if}
        {/if}
        
        {#if dialogMode == 'send'}
          {#if awaitingDefaultsResponse}
            <div class='flex flex-row gap-2 text-sm items-center'>
              <i class="fa-solid fa-spinner fa-spin text-gray-400 h-max" />
              <div>Getting Activity Fields...</div>
            </div>
          {:else}
            <section class="space-y-1">
              <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Name</h3>
              <Input type="text" bind:value={name} placeholder='Activity Name' />
            </section>
            <section class="space-y-1">
              <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Description</h3>
              <Textarea class='min-h-[100px]' bind:value={description} placeholder='Activity Description' />
            </section>
            <div class='flex flex-col gap-0'>
              {#if distanceVal > 0}
                <div class="flex flex-row gap-2">
                  <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Distance:</h3>
                  <span class="text-sm font-medium">{distanceVal.toFixed(1)} {distanceUnit}</span>
                </div>
              {/if}
              {#if durationStr}
                <div class="flex flex-row gap-2">
                  <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Duration:</h3>
                  <span class="text-sm font-medium">{durationStr}</span>
                </div>
              {/if}
              {#if avgSpeed || maxSpeed}
                <div class="flex flex-row gap-2">
                  <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Speed:</h3>
                  <span class="text-sm font-medium">
                    {#if avgSpeed && maxSpeed}
                      {avgSpeed.toFixed(1)} / {maxSpeed.toFixed(1)} {speedUnit}
                    {:else if avgSpeed}
                      {avgSpeed.toFixed(1)} {speedUnit}
                    {:else}
                      {maxSpeed.toFixed(1)} {speedUnit}
                    {/if}
                  </span>
                </div>
              {/if}
              {#if avgPower || maxPower}
                <div class="flex flex-row gap-2">
                  <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Power:</h3>
                  <span class="text-sm font-medium">
                    {#if avgPower && maxPower}
                      {avgPower} W / {maxPower} W
                    {:else if avgPower}
                      {avgPower} W
                    {:else}
                      {maxPower} W
                    {/if}
                  </span>
                </div>
              {/if}
            </div>
          {/if}
        {/if}

        {#if (!awaitingDefaultsResponse && fieldDefaults) || dialogMode == 'update_report'}
          <div class='flex flex-col gap-3'>
            {#if dialogMode !== 'update_report'}
              <div class="flex flex-row items-center gap-3">
                <Checkbox id="report" bind:checked={includeReport} />
                <Label class='m-0' for="report">Include Xert report image</Label>
              </div>
            {/if}
            {#if showDesigner}
              <ReportDesigner path={activity.path} initialType={fieldDefaults?.body_params?.report_type ?? null} bind:config={reportConfig} />
            {/if}
          </div>
        {/if}
      </div>
      <div class="flex flex-row gap-2 justify-end px-5 py-4 border-t border-border">
        <Button size='sm' variant='outline' on:click={() => dialogOpen = false}>Cancel</Button>
        <Button size='sm' class='bg-orange-500 hover:bg-orange-400' disabled={awaitingStatusResponse || awaitingDefaultsResponse || (showDesigner && !reportConfig)} on:click={onSend}>
          <i class={`fa-solid ${sendButtonIcon} text-xs mr-1`} /> {dialogMode == 'update_report' ? 'Update Report' : 'Send to Strava'}
        </Button>
      </div>
    </Dialog.Content>
  </Dialog.Root>
{/if}
